import { Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import DidNotParticipate from '../../resources/img/didNotParticipate.svg';
import Failed from '../../resources/img/failed.svg';
import Participated from '../../resources/img/participated.svg';
import Passed from '../../resources/img/passed.svg';
import { formatSessionTimeRange } from '../utils/openClass';
import { ClassAttendeeSession } from '../utils/openClassAttendees';
import {
  SessionAttendeeStatus,
  sessionAttendeeStatus,
} from '../utils/sessionAttendees';

const presentation: Record<
  SessionAttendeeStatus,
  { className: string; icons: string[] }
> = {
  passed: {
    className: 'border-success bg-successLight text-success',
    icons: [Participated, Passed],
  },
  failed: {
    className: 'border-warningRed bg-offwhiteDark text-warningRed',
    icons: [Participated, Failed],
  },
  present: {
    className: 'border-safeLifeMedium bg-blueGrayLightest text-safeLifeMedium',
    icons: [Participated],
  },
  partlyPresent: {
    className: 'border-orange-600 bg-blueGrayLightest text-orange-600',
    icons: [Participated],
  },
  notPresent: {
    className: 'border-warningRed bg-offwhiteDark text-warningRed',
    icons: [DidNotParticipate],
  },
  notRecorded: {
    className: 'border-grayMedium bg-blueGrayLightest text-grayMedium',
    icons: [],
  },
};

const OpenClassSessionStatusTags: React.FC<{
  className?: string;
  sessions: ClassAttendeeSession[];
}> = ({ className = '', sessions }) => {
  const { t } = useTranslation();

  if (sessions.length === 0) {
    return <span className="text-xs text-grayMedium">–</span>;
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {sessions.map((session) => {
        const status = sessionAttendeeStatus({
          attendanceStatus: session.attendanceStatus,
          sessionResult: session.result,
        });
        const { className: tagClassName, icons } = presentation[status];
        const statusLabel = `${formatSessionTimeRange(session)} · ${t(
          `components.OpenClassAttendeeStatusBox.${status}`,
        )}`;

        return (
          <Tooltip key={session.sessionId} title={statusLabel}>
            <Tag
              aria-label={statusLabel}
              role="img"
              className={`m-0 inline-flex items-center gap-1 rounded-full ${tagClassName}`}>
              {icons.map((icon) => (
                <img alt="" className="w-3" key={icon} src={icon} />
              ))}
              {dayjs(session.startAt).format('D MMM')}
            </Tag>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default OpenClassSessionStatusTags;
